import FnEditorElement from "./FnEditorElement";
import { FnEditorElementType } from "./types";
export default class FnEditorElementMap {
    constructor(editorIds, logger, lines = []) {
        this.editorIds = editorIds;
        this.logger = logger;
        this.positions = [];
        this.map = new Map();
        for (const line of lines) {
            this.positions.push(line.id);
            this.map.set(line.id, new FnEditorElement(line.id, line.text));
        }
        this.ensureBlankAtBothEnds();
        this.evaluateTypes(0, this.positions.length - 1);
    }
    getFirst() {
        return this.map.get(this.positions[0]);
    }
    getLast() {
        return this.map.get(this.positions[this.positions.length - 1]);
    }
    getAll() {
        return this.positions.map((id) => {
            return this.map.get(id);
        });
    }
    getNextOf(id) {
        const index = this.getIndexOfId(id);
        if (this.positions[index + 1]) {
            return this.getById(this.positions[index + 1]);
        }
        return null;
    }
    getPreviousOf(id) {
        const index = this.getIndexOfId(id);
        if (this.positions[index - 1]) {
            return this.getById(this.positions[index - 1]);
        }
        return null;
    }
    getById(id) {
        return this.map.get(id) || null;
    }
    removeById(id) {
        const index = this.getIndexOfId(id);
        this.positions.splice(index, 1);
        this.map.delete(id);
    }
    getByIndex(index) {
        return this.positions[index] ? this.getById(this.positions[index]) : null;
    }
    getIndexOfId(id) {
        return this.positions.findIndex((candidate) => {
            return candidate === id;
        });
    }
    ensureBlankAtBothEnds() {
        if (this.positions.length === 0) {
            this.insertAfter(new FnEditorElement(this.editorIds.createId(), ''));
            this.insertAfter(new FnEditorElement(this.editorIds.createId(), ''));
            return;
        }
        if (this.getFirst().getIntrinsicType() !== FnEditorElementType.Blank) {
            this.insertAfter(new FnEditorElement(this.editorIds.createId(), ''));
        }
        const last = this.getLast();
        if (last.getIntrinsicType() !== FnEditorElementType.Blank) {
            this.insertAfter(new FnEditorElement(this.editorIds.createId(), ''), last.getId());
        }
    }
    insertAfter(element, afterId) {
        const id = element.getId();
        this.map.set(id, element);
        if (!afterId) {
            this.positions = [id, ...this.positions];
            return;
        }
        const index = this.getIndexOfId(afterId);
        this.positions.splice(index + 1, 0, id);
    }
    /**
     * Returns the first and last ID by position from evaluateIds
     */
    findMinMaxIndices(evaluateIds) {
        let minIndex = Infinity;
        let maxIndex = -Infinity;
        let minId = null;
        let maxId = null;
        for (const id of evaluateIds) {
            const index = this.positions.indexOf(id);
            if (index !== -1) { // Ensure the ID exists in positions
                if (index < minIndex) {
                    minIndex = index;
                    minId = id;
                }
                if (index > maxIndex) {
                    maxIndex = index;
                    maxId = id;
                }
            }
        }
        return { minId, maxId };
    }
    evaluateIds(evaluateIds) {
        if (evaluateIds.length === 0) {
            return { elementsWithChangedType: [] };
        }
        const { minId, maxId } = this.findMinMaxIndices(evaluateIds);
        if (minId === maxId && minId !== null) {
            return this.evaluateTypeOfId(minId);
        }
        if (minId === null && maxId === null) {
            const changed = [];
            evaluateIds.forEach((id) => {
                changed.push(...this.evaluateTypeOfId(id).elementsWithChangedType);
            });
            return { elementsWithChangedType: changed };
        }
        if (minId === null || maxId === null) {
            return this.evaluateTypeOfId(minId || maxId || '');
        }
        const startIndex = this.getIndexOfPreviousBlankLine(minId);
        const endIndex = this.getIndexOfNextBlankLine(maxId);
        return this.evaluateTypes(startIndex, endIndex);
    }
    getIndexOfPreviousBlankLine(elementId) {
        let startIndex = -1;
        let element = this.getById(elementId);
        let previousElement = element;
        while (previousElement) {
            previousElement = this.getPreviousOf(previousElement.getId());
            if ((previousElement === null || previousElement === void 0 ? void 0 : previousElement.getIntrinsicType()) === FnEditorElementType.Blank) {
                startIndex = this.getIndexOfId(previousElement.getId());
                break;
            }
        }
        return startIndex;
    }
    getIndexOfNextBlankLine(elementId) {
        let endIndex = this.positions.length - 1;
        let element = this.getById(elementId);
        let nextElement = element;
        while (nextElement) {
            nextElement = this.getNextOf(nextElement.getId());
            if ((nextElement === null || nextElement === void 0 ? void 0 : nextElement.getIntrinsicType()) === FnEditorElementType.Blank) {
                endIndex = this.getIndexOfId(nextElement.getId());
                break;
            }
        }
        return endIndex;
    }
    evaluateTypeOfId(changedElementId) {
        let startIndex = this.getIndexOfPreviousBlankLine(changedElementId);
        let endIndex = this.getIndexOfNextBlankLine(changedElementId);
        return this.evaluateTypes(startIndex, endIndex);
    }
    evaluateTypes(startIndex, endIndex) {
        const elementsWithChangedType = [];
        while (startIndex <= endIndex) {
            const next = this.getByIndex(startIndex + 1);
            const prev = this.getByIndex(startIndex - 1);
            const element = this.getByIndex(startIndex);
            if (element) {
                const prevType = (prev === null || prev === void 0 ? void 0 : prev.getType()) || FnEditorElementType.Blank;
                const nextIntrinsicType = (next === null || next === void 0 ? void 0 : next.getIntrinsicType()) || FnEditorElementType.Blank;
                const result = element.evaluateType(prevType, nextIntrinsicType);
                this.logger.log(`Evaluate element`, element, 'with prev type ', prevType, ' and next instrinsic type ', nextIntrinsicType, ' with result ', result);
                if (result.typeAfter !== result.typeBefore) {
                    elementsWithChangedType.push(element);
                }
            }
            startIndex++;
        }
        return { elementsWithChangedType: elementsWithChangedType };
    }
    toRawLines() {
        return this.getAll().map((element) => {
            return {
                id: element.getId(),
                text: element.getText()
            };
        });
    }
}
